import { create } from 'zustand';
import { toast } from 'sonner';
import { useAuthStore } from './authStore';
import { supabase } from '@/lib/supabase';
// Define types locally since they're not yet exported from core
export type RuleField = 'description' | 'category' | 'payee' | 'notes' | 'tags';
export type RuleOperator = 'contains' | 'equals' | 'startsWith' | 'endsWith';

export interface RuleAction {
  field: RuleField;
  value: string;
}

export interface Rule {
  id: string;
  user_id: string;
  name: string;
  active: boolean;
  condition: {
    field: RuleField;
    operator: RuleOperator;
    value: string;
  };
  conditions?: {
    field: RuleField;
    operator: RuleOperator;
    value: string;
  };
  actions: RuleAction[];
  created_at: string;
  updated_at: string;
}

interface RulesState {
  rules: Rule[];
  isLoading: boolean;
  error: string | null;
}

export interface WebRulesStore extends RulesState {
  fetchRules: () => Promise<void>;
  addRule: (rule: Omit<Rule, 'id' | 'created_at' | 'updated_at'>) => Promise<Rule | undefined>;
  updateRule: (ruleId: string, updates: Partial<Rule>) => Promise<void>;
  deleteRule: (ruleId: string) => Promise<void>;
  applyRules: (transaction: any) => any; // Using any temporarily until we have the Transaction type
}

export const useRulesStore = create<WebRulesStore>((set, get) => ({
  rules: [],
  isLoading: false,
  error: null,

  fetchRules: async () => {
    try {
      console.log('fetchRules called');
      set({ isLoading: true, error: null });
      
      // Access the user directly from the auth store
      const user = useAuthStore.getState().user;
      
      if (!user?.id) {
        console.error('User not authenticated');
        set({ isLoading: false });
        return;
      }
      
      console.log('Fetching rules for user:', user.id);
      
      const { data, error } = await supabase
        .from('rules')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      console.log('Supabase response for fetchRules:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      // Map the data from Supabase schema to our app's structure
      const mappedRules: Rule[] = data?.map(rule => ({
        id: rule.id,
        user_id: rule.user_id,
        name: rule.name,
        active: rule.is_active,
        condition: rule.conditions, // Map 'conditions' from DB to 'condition' for frontend
        actions: rule.actions,
        created_at: rule.created_at,
        updated_at: rule.updated_at
      })) || [];
      
      console.log('Mapped rules:', mappedRules);
      
      set({ rules: mappedRules, isLoading: false });
    } catch (error) {
      console.error('Error fetching rules:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch rules', isLoading: false });
      toast.error('Failed to fetch rules');
    }
  },

  addRule: async (rule) => {
    try {
      console.log('rulesStore.addRule called with:', rule);
      set({ isLoading: true, error: null });
      
      // Access the user directly from the auth store
      const user = useAuthStore.getState().user;
      
      if (!user?.id) {
        console.error('User not authenticated');
        set({ isLoading: false });
        return undefined;
      }
      
      console.log('Current user:', user);
      
      // Map the rule object to match Supabase schema
      const supabaseRule = {
        user_id: user.id,
        name: rule.name,
        is_active: rule.active,
        conditions: rule.condition, // Map condition to conditions
        actions: rule.actions,
        priority: 0, // Default priority
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log('Prepared rule for Supabase:', supabaseRule);
      
      const { data, error } = await supabase
        .from('rules')
        .insert([supabaseRule])
        .select()
        .single();
      
      console.log('Supabase response:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      // Map the returned data back to our app's structure
      const newRule: Rule = {
        id: data.id,
        user_id: data.user_id,
        name: data.name,
        active: data.is_active,
        condition: data.conditions,
        actions: data.actions,
        created_at: data.created_at,
        updated_at: data.updated_at
      };
      
      set(state => ({
        rules: [newRule, ...state.rules],
        isLoading: false
      }));
      
      toast.success('Rule created successfully');
      return newRule;
    } catch (error) {
      console.error('Error adding rule:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create rule', isLoading: false });
      toast.error('Failed to create rule');
      throw error;
    }
  },

  updateRule: async (ruleId, updates) => {
    try {
      console.log('updateRule called with:', { ruleId, updates });
      set({ isLoading: true, error: null });
      
      // Map the updates to match Supabase schema
      const supabaseUpdates: any = {};
      
      if (updates.name !== undefined) {
        supabaseUpdates.name = updates.name;
      }
      
      if (updates.active !== undefined) {
        supabaseUpdates.is_active = updates.active;
      }
      
      if (updates.condition !== undefined) {
        supabaseUpdates.conditions = updates.condition;
      }
      
      if (updates.actions !== undefined) {
        supabaseUpdates.actions = updates.actions;
      }
      
      supabaseUpdates.updated_at = new Date().toISOString();
      
      console.log('Mapped updates for Supabase:', supabaseUpdates);
      
      const { data, error } = await supabase
        .from('rules')
        .update(supabaseUpdates)
        .eq('id', ruleId)
        .select()
        .single();
      
      console.log('Supabase response for updateRule:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      // Map the returned data back to our app's structure
      const updatedRule: Rule = {
        id: data.id,
        user_id: data.user_id,
        name: data.name,
        active: data.is_active,
        condition: data.conditions,
        actions: data.actions,
        created_at: data.created_at,
        updated_at: data.updated_at
      };
      
      set(state => ({
        rules: state.rules.map(rule => 
          rule.id === ruleId ? updatedRule : rule
        ),
        isLoading: false
      }));
      
      toast.success('Rule updated successfully');
    } catch (error) {
      console.error('Error updating rule:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update rule', isLoading: false });
      toast.error('Failed to update rule');
      throw error;
    }
  },

  deleteRule: async (ruleId) => {
    try {
      console.log('deleteRule called with ruleId:', ruleId);
      set({ isLoading: true, error: null });
      
      const { error } = await supabase
        .from('rules')
        .delete()
        .eq('id', ruleId);
      
      console.log('Supabase response for deleteRule:', { error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      set(state => ({
        rules: state.rules.filter(rule => rule.id !== ruleId),
        isLoading: false
      }));
      
      toast.success('Rule deleted successfully');
    } catch (error) {
      console.error('Error deleting rule:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete rule', isLoading: false });
      toast.error('Failed to delete rule');
      throw error;
    }
  },
  
  applyRules: (transaction) => {
    const { rules } = get();
    
    console.log('[RULES DEBUG] Starting rule application with:', {
      transaction,
      totalRules: rules.length
    });
    
    if (!rules.length) {
      console.log('[RULES DEBUG] No rules found, returning original transaction');
      return transaction;
    }

    let result = { ...transaction };
    
    // Only apply active rules
    const activeRules = rules.filter(rule => rule.active);
    console.log('[RULES DEBUG] Active rules found:', activeRules.length);
    
    for (const rule of activeRules) {
      console.log('[RULES DEBUG] Processing rule:', rule.name);
      
      // Note: After Supabase migration, the condition is properly mapped from 'conditions' to 'condition'
      const condition = rule.condition;
      const { actions } = rule;
      
      console.log('[RULES DEBUG] Rule condition:', condition);
      console.log('[RULES DEBUG] Rule actions:', actions);
      
      if (!condition || !actions) {
        console.log('[RULES DEBUG] Skipping rule - missing condition or actions');
        continue;
      }
      
      // Map rule field names to transaction property names
      const fieldMap = {
        description: 'description',
        category: 'categoryId',
        payee: 'payee',
        notes: 'notes',
        tags: 'tags'
      };
      
      // Get the correct transaction property based on the rule field
      const transactionField = fieldMap[condition.field] || condition.field;
      
      // Check if the condition matches
      let matches = false;
      const fieldValue = transaction[transactionField] || '';
      
      console.log('[RULES DEBUG] Checking condition:', {
        field: condition.field,
        transactionField,
        fieldValue,
        conditionValue: condition.value,
        operator: condition.operator
      });
      
      if (typeof fieldValue === 'string') {
        switch (condition.operator) {
          case 'contains':
            matches = fieldValue.toLowerCase().includes(condition.value.toLowerCase());
            break;
          case 'equals':
            matches = fieldValue.toLowerCase() === condition.value.toLowerCase();
            break;
          case 'startsWith':
            matches = fieldValue.toLowerCase().startsWith(condition.value.toLowerCase());
            break;
          case 'endsWith':
            matches = fieldValue.toLowerCase().endsWith(condition.value.toLowerCase());
            break;
        }
      } else if (Array.isArray(fieldValue) && condition.field === 'tags') {
        // Handle array fields like tags
        matches = fieldValue.some(tag => tag === condition.value);
      }
      
      console.log('[RULES DEBUG] Condition match result:', matches);
      
      // If the condition matches, apply the actions
      if (matches) {
        console.log('[RULES DEBUG] Applying actions for matched rule:', rule.name);
        for (const action of actions) {
          console.log('[RULES DEBUG] Applying action:', action);
          // Apply the action to the transaction based on field type
          switch (action.field) {
            case 'category':
              console.log('[RULES DEBUG] Setting categoryId from', result.categoryId, 'to', action.value);
              result.categoryId = action.value;
              break;
            case 'payee':
              result.payee = action.value;
              break;
            case 'notes':
              result.notes = action.value;
              break;
            case 'tags':
              // Ensure tags array exists
              if (!result.tags) {
                result.tags = [];
              }
              // Add tag if it doesn't exist
              if (!result.tags.includes(action.value)) {
                result.tags.push(action.value);
              }
              break;
            default:
              // For other fields, use the mapped property name if available
              const resultField = fieldMap[action.field] || action.field;
              result[resultField] = action.value;
          }
        }
      }
    }
    
    console.log('[RULES DEBUG] Final result after applying rules:', result);
    return result;
  }
}));
